import type { Ref } from 'react';
import React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import type { CardProps, CardHeaderProps } from '@mui/material';
import { Card, CardContent, CardHeader, Typography } from '@mui/material';

// project imports
import type { KeyedObject } from 'types';

// constant
const headerSX = {
  '& .MuiCardHeader-action': { mr: 0 },
};

// Adapted from berrydashboard.io
export interface MainCardProps extends KeyedObject {
  border?: boolean;
  boxShadow?: boolean;
  children: React.ReactNode | string;
  style?: React.CSSProperties;
  content?: boolean;
  className?: string;
  contentClass?: string;
  darkTitle?: boolean;
  sx?: CardProps['sx'];
  secondary?: CardHeaderProps['action'];
  shadow?: string;
  elevation?: number;
  title?: React.ReactNode | string;
}

export const MainCard = React.forwardRef(
  (
    {
      border = true,
      boxShadow,
      children,
      content = true,
      darkTitle,
      secondary,
      shadow,
      sx = {},
      title,
      ...others
    }: MainCardProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const theme = useTheme();

    return (
      <Card
        ref={ref}
        {...others}
        sx={[
          (theme) => ({
            borderColor: theme.palette.primary[200] + 75,
            ...sx,
            ...theme.applyStyles('dark', {
              borderColor: theme.palette.background.default,
            }),
          }),
          border
            ? {
                border: '1px solid',
              }
            : {
                border: 'none',
              },
          boxShadow
            ? {
                ':hover': {
                  boxShadow:
                    shadow ||
                    (theme.palette.mode === 'dark'
                      ? '0 2px 14px 0 rgb(33 150 243 / 10%)'
                      : '0 2px 14px 0 rgb(32 40 45 / 8%)'),
                },
              }
            : {
                ':hover': {
                  boxShadow: 'inherit',
                },
              },
        ]}>
        {/* card header and action */}
        {!darkTitle && title && (
          <CardHeader sx={headerSX} title={title} action={secondary} />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={headerSX}
            title={<Typography variant="h3">{title}</Typography>}
            action={secondary}
          />
        )}
        {/* card content */}
        {content && <CardContent sx={{ p: 4 }}>{children}</CardContent>}
        {!content && children}
      </Card>
    );
  }
);

MainCard.displayName = 'MainCard';
